import React, { useState, useEffect } from "react";
import { Input, Button, Select, Space, List, Layout, message } from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import "./SearchPage.css";

const { Header, Content } = Layout;
const { Option } = Select;

// Base API URL - for production deployment
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://arxed.info";

function SearchPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // County filter states
  const [countyOptions, setCountyOptions] = useState([]);
  const [selectedCounties, setSelectedCounties] = useState([]);
  const [countyFilterVisible, setCountyFilterVisible] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);

  // Check authentication when component mounts
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Load counties data when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchCounties();
    }
  }, [isAuthenticated]);

  // Check if user is authenticated
  const checkAuthentication = async () => {
    try {
      await api.get("/api/health");
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Authentication error:", error);
      // Redirect to login page if not authenticated
      navigate("/");
    }
  };

  // Function to fetch counties list
  const fetchCounties = async () => {
    setLoadingCounties(true);
    try {
      const response = await api.get("/api/counties");
      const sortedCounties = [...(response.data || [])].sort();
      setCountyOptions(sortedCounties);
    } catch (error) {
      console.error("Failed to fetch counties:", error);

      // Handle authentication error
      if (error.response && error.response.status === 302) {
        navigate("/");
        return;
      }

      message.error("Failed to retrieve county list");
    } finally {
      setLoadingCounties(false);
    }
  };

  // Toggle county filter visibility
  const toggleCountyFilter = () => {
    setCountyFilterVisible(!countyFilterVisible);
  };

  const handleCountyFilterChange = (value) => {
    setSelectedCounties(value);
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      message.warning("Please enter a search keyword");
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await api.post("/api/search", {
        query: query,
        counties: selectedCounties,
      });
      setResults(response.data);
      if (response.data.length === 0) {
        message.info("No matching results found");
      }
    } catch (error) {
      console.error("Search error:", error);

      // Handle authentication error
      if (error.response && error.response.status === 302) {
        navigate("/");
        return;
      }

      if (error.response) {
        if (error.response.status === 401) {
          message.error("Your session has expired, please log in again");
          navigate("/");
        } else {
          message.error("Search failed, please try again later");
        }
      } else {
        message.error("Connection error, please check your network");
      }
    } finally {
      setLoading(false);
    }
  };

  // PDF preview compatibility detection
  const isPDFPreviewSupported = () => {
    const ua = window.navigator.userAgent;
    // Exclude mobile and IE
    return (
      !/Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
        ua
      ) && !/MSIE|Trident/.test(ua)
    );
  };

  // View PDF function
  const viewPdf = (fileId, page) => {
    if (!isPDFPreviewSupported()) {
      message.warning(
        "Your browser may download the PDF instead of previewing"
      );
    }
    const loadingMessage = message.loading("Loading PDF...", 0);

    api
      .get(`/api/pdf_url/${encodeURIComponent(fileId)}.pdf`, {
        params: { page: page },
      })
      .then((response) => {
        loadingMessage();
        const pdfUrl = response.data.url;
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
      })
      .catch((error) => {
        loadingMessage();

        // Handle authentication error
        if (error.response && error.response.status === 302) {
          navigate("/");
          return;
        }

        if (error.response) {
          switch (error.response.status) {
            case 400:
              message.error("Invalid page number");
              break;
            case 404:
              message.error("PDF file not found in storage");
              break;
            case 403:
              message.error("Access denied to PDF file");
              break;
            default:
              message.error("Failed to load PDF");
          }
        } else {
          message.error("Network error");
        }
      });
  };

  return (
    <Layout className="search-layout">
      <Header className="search-header">
        <div className="logo-container">
          <img
            src={`${process.env.PUBLIC_URL}/logo.webp`}
            alt="Company Logo"
            className="small-logo"
          />
        </div>
      </Header>
      <Content className="search-content">
        <div className="search-container">
          <Space direction="vertical" className="search-box">
            <h2>Contract Content Search</h2>
            <div className="search-controls">
              <Space direction="horizontal">
                <Input
                  placeholder="Enter search keywords"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onPressEnter={handleSearch}
                  prefix={<SearchOutlined />}
                  className="search-input"
                />
                <Button type="primary" onClick={handleSearch} loading={loading}>
                  Search
                </Button>
                <Button
                  onClick={toggleCountyFilter}
                  loading={loadingCounties}
                  icon={<FilterOutlined />}
                  type={countyFilterVisible ? "primary" : "default"}
                >
                  County Filter
                </Button>
              </Space>
            </div>

            {countyFilterVisible && (
              <div className="filter-container">
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Select counties to search (empty for all)"
                  value={selectedCounties}
                  onChange={handleCountyFilterChange}
                  optionFilterProp="children"
                  allowClear
                  loading={loadingCounties}
                >
                  {countyOptions.map((county) => (
                    <Option key={county} value={county}>
                      {county}
                    </Option>
                  ))}
                </Select>
              </div>
            )}
          </Space>

          <div className="results-container">
            {results.length === 0 ? (
              <div className="empty-results">
                {hasSearched ? (
                  <p>
                    No results found. This is because there are no files in the
                    selected counties.
                  </p>
                ) : (
                  <>
                    <p>Welcome to the Contract Search System</p>
                    <p>Please enter keywords above to start searching</p>
                    <p>e.g. searching for "science lab class size limits"</p>
                  </>
                )}
              </div>
            ) : (
              <List
                itemLayout="vertical"
                dataSource={results}
                renderItem={(result, index) => {
                  const score =
                    typeof result.score === "number" ? result.score : 0;
                  return (
                    <List.Item key={index} className="result-item">
                      <div className="result-header">
                        <span className="result-number">
                          {result.county && <div>County: {result.county}</div>}
                          {result.district && (
                            <div>District: {result.district}</div>
                          )}
                          {result.filename && (
                            <div>File: {result.filename}</div>
                          )}
                        </span>
                      </div>
                      {result.context_before && (
                        <div className="context-before">
                          ...{result.context_before}
                        </div>
                      )}

                      <div className="matched-text">
                        <strong>Matched Paragraph:</strong> {result.text}
                      </div>

                      {result.context_after && (
                        <div className="context-after">
                          {result.context_after}...
                        </div>
                      )}

                      <div className="result-actions">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => viewPdf(result.file_id, result.page)}
                        >
                          View Contract at Page {result.page}
                        </Button>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </div>
      </Content>
    </Layout>
  );
}

export default SearchPage;
